import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BottomModal from '@components/_common/bottom-modal/BottomModal';
import ProfileImage from '@components/_common/profile-image/ProfileImage';
import { Layout, Typo } from '@design-system';
import { useTrackEvent } from '@hooks/useTrackEvent';
import { Connection } from '@models/api/friends';
import { User } from '@models/user';
import { getFriendList } from '@utils/apis/user';
import * as S from './ViewAsPicker.styled';

export type ViewAsSelection = { kind: 'tier'; tier: 'public' } | { kind: 'user'; username: string };

interface ViewAsPickerProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (selection: ViewAsSelection) => void;
}

function ViewAsPicker({ visible, onClose, onSelect }: ViewAsPickerProps) {
  const [t] = useTranslation('translation', { keyPrefix: 'view_as.picker' });
  const [friends, setFriends] = useState<User[] | null>(null);
  const trackEvent = useTrackEvent();

  useEffect(() => {
    if (!visible || friends !== null) return;
    let cancelled = false;
    getFriendList()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray((res as any).results)
          ? (res as any).results
          : (res as unknown as User[]);
        setFriends(list);
      })
      .catch(() => {
        if (!cancelled) setFriends([]);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, friends]);

  const closeFriends = (friends ?? []).filter(
    (f) => f.connection_status === Connection.CLOSE_FRIEND,
  );
  const regularFriends = (friends ?? []).filter((f) => f.connection_status === Connection.FRIEND);

  const handleSelectPublic = () => {
    // Picker-level event: "user chose public" (tier-specific). Distinct
    // from the page-level `view_as_audience_changed` because that fires
    // on URL-param change, including initial mount with the default tier.
    // This event ONLY fires when the user explicitly tapped Public in the
    // picker — the engagement intent signal.
    trackEvent('view_as_audience_picked', { kind: 'tier', tier: 'public' });
    onSelect({ kind: 'tier', tier: 'public' });
    onClose();
  };

  const handleSelectFriend = (username: string, isCloseFriend: boolean) => {
    // is_close_friend distinguishes 'checking what intimate people see'
    // from 'checking what acquaintances see' — different privacy signals.
    // Username itself is intentionally NOT logged (high cardinality and
    // analytically not useful — what matters is the relationship category).
    trackEvent('view_as_audience_picked', {
      kind: 'user',
      is_close_friend: isCloseFriend ? 'true' : 'false',
    });
    onSelect({ kind: 'user', username });
    onClose();
  };

  return (
    <BottomModal visible={visible} onClose={onClose} heightMode="full" draggable>
      <div style={{ width: '100%', backgroundColor: '#FCFCFC', borderBottom: '1px solid #F0F0F0' }}>
        <Layout.FlexRow w="100%" h={44} alignItems="center" justifyContent="center">
          <Typo type="title-medium" bold>
            {t('title')}
          </Typo>
        </Layout.FlexRow>
      </div>
      <Layout.FlexCol w="100%" p={16} gap={16}>
        {/* Public section */}
        <Layout.FlexCol w="100%" gap={8}>
          <Typo type="label-medium" color="MEDIUM_GRAY">
            {t('public_section')}
          </Typo>
          <S.Row onClick={handleSelectPublic}>
            <S.PublicAvatar>🌐</S.PublicAvatar>
            <Typo type="body-medium" color="BLACK">
              {t('public_label')}
            </Typo>
          </S.Row>
        </Layout.FlexCol>

        {/* Loading state */}
        {friends === null && (
          <Typo type="body-medium" color="MEDIUM_GRAY">
            {t('loading')}
          </Typo>
        )}

        {/* Close Friends section */}
        {closeFriends.length > 0 && (
          <Layout.FlexCol w="100%" gap={8}>
            <Typo type="label-medium" color="MEDIUM_GRAY">
              {t('close_friends_section')}
            </Typo>
            {closeFriends.map((f) => (
              <S.Row key={f.id} onClick={() => handleSelectFriend(f.username, true)}>
                <ProfileImage imageUrl={f.profile_image} username={f.username} size={32} />
                <Typo type="body-medium" color="BLACK">
                  {f.username}
                </Typo>
              </S.Row>
            ))}
          </Layout.FlexCol>
        )}

        {/* Regular Friends section */}
        {regularFriends.length > 0 && (
          <Layout.FlexCol w="100%" gap={8}>
            <Typo type="label-medium" color="MEDIUM_GRAY">
              {t('friends_section')}
            </Typo>
            {regularFriends.map((f) => (
              <S.Row key={f.id} onClick={() => handleSelectFriend(f.username, false)}>
                <ProfileImage imageUrl={f.profile_image} username={f.username} size={32} />
                <Typo type="body-medium" color="BLACK">
                  {f.username}
                </Typo>
              </S.Row>
            ))}
          </Layout.FlexCol>
        )}

        {/* Empty state when fetched but no friends */}
        {friends !== null && closeFriends.length === 0 && regularFriends.length === 0 && (
          <Typo type="body-medium" color="MEDIUM_GRAY">
            {t('empty_friends')}
          </Typo>
        )}
      </Layout.FlexCol>
    </BottomModal>
  );
}

export default ViewAsPicker;
